import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { themedPrompt } from '@/lib/themed-dialog';
import { Bold, Italic, Underline, Link2, Palette, Type as TypeIcon, Minus, Plus, Copy } from 'lucide-react';

/**
 * Inline editing UX helpers — Squarespace-style block hover behaviour.
 *  - When the cursor is over an editable text node → text-caret cursor + click-to-edit
 *  - When over the block's whitespace/edges → grab cursor + drag indicator
 *  - When dragging blocks → uses the existing DnD context
 */

export function EditableHoverBoundary({ children }) {
  // Renders a thin guideline + hover-zone wrapper. Used inside each block.
  return (
    <div className="relative isolate" data-editable-section>
      {children}
      <style>{`
        /* Iter 160 — Show cursor:text only when actively editing (:focus
           or after user has clicked-in via data-wb-active-edit). At rest
           we want cursor:grab from .wb-inline-text so users see they can
           freely drag the element on the canvas. */
        [data-editable-section] [contenteditable=true] {
          outline: none;
        }
        [data-editable-section] [contenteditable=true]:focus,
        [data-editable-section] [data-wb-active-edit='1'] {
          cursor: text;
        }
        /* Iter 189i — Hover-only visual feedback. Removed the persistent
           violet background + inset ring on :focus. Clicking a text now
           enters edit mode WITHOUT any surrounding highlight — the caret
           itself is the only feedback. Hover feedback is provided by the
           .wb-inline-text hover outline rule in GridOverlay. */
        [data-editable-section] [contenteditable=true]:focus {
          background: transparent;
          box-shadow: none;
          border-radius: 4px;
        }
        [data-editable-section] .wb-image-zone {
          cursor: pointer;
        }
        [data-editable-section] .wb-block-grab {
          cursor: grab;
        }
        [data-editable-section] .wb-block-grab:active {
          cursor: grabbing;
        }
      `}</style>
    </div>
  );
}

/**
 * Floating selection toolbar that appears above any contenteditable region.
 * Iter 198 — Type Popover Polish: per-word (per-selection) rich formatting.
 * Bold / Italic / Underline / Color / Size / Link / Duplicate. All commands
 * operate on the current DOM Selection so users can format single words.
 */
export function InlineSelectionToolbar() {
  const [state, setState] = useState({ visible: false, top: 0, left: 0 });
  const [openPop, setOpenPop] = useState(null); // 'color' | 'size'
  const savedRangeRef = useRef(null);

  // Preserve the selection while the popover is open (clicking a swatch would
  // otherwise blur the caret and collapse the range).
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
  };
  const restoreSelection = () => {
    const sel = window.getSelection();
    if (!sel || !savedRangeRef.current) return;
    sel.removeAllRanges();
    sel.addRange(savedRangeRef.current);
  };

  useEffect(() => {
    const compute = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setState(s => ({ ...s, visible: false }));
        setOpenPop(null);
        return;
      }
      const range = sel.getRangeAt(0);
      let node = range.commonAncestorContainer;
      while (node && node !== document.body) {
        if (node.nodeType === 1 && node.getAttribute && node.getAttribute('contenteditable') === 'true') break;
        node = node.parentNode;
      }
      if (!node || node === document.body) {
        setState(s => ({ ...s, visible: false }));
        setOpenPop(null);
        return;
      }
      const r = range.getBoundingClientRect();
      if (!r.width && !r.height) return;
      // Position ABOVE the selection when there's room, else below.
      const TOOLBAR_H = 44;
      const flipDown = r.top < (TOOLBAR_H + 16);
      const top = flipDown
        ? Math.min(window.innerHeight - TOOLBAR_H - 8, r.bottom + 8)
        : Math.max(8, r.top - TOOLBAR_H - 8);
      setState({ visible: true, top, left: r.left + r.width / 2 });
    };
    const onSelect = () => compute();
    document.addEventListener('selectionchange', onSelect);
    document.addEventListener('mouseup', onSelect);
    // Close popovers on Escape
    const onKey = (e) => { if (e.key === 'Escape') setOpenPop(null); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('selectionchange', onSelect);
      document.removeEventListener('mouseup', onSelect);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!state.visible) return null;

  const exec = (cmd, val) => {
    restoreSelection();
    try { document.execCommand(cmd, false, val); } catch (e) { /* noop */ }
    saveSelection();
  };

  // Wrap current selection in a <span style="font-size:...px"> — execCommand's
  // fontSize command only supports 1-7 legacy sizes which are useless.
  const applyInlineStyle = (styleObj) => {
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // Guard against empty ranges after clicking popover
    if (range.collapsed) return;
    const span = document.createElement('span');
    Object.entries(styleObj).forEach(([k, v]) => { span.style[k] = v; });
    try {
      range.surroundContents(span);
    } catch (err) {
      // Selection crosses element boundaries — fall back to extractContents
      const frag = range.extractContents();
      span.appendChild(frag);
      range.insertNode(span);
    }
    // Re-select the styled span so subsequent formats stack on the same range.
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    sel.removeAllRanges();
    sel.addRange(newRange);
    saveSelection();
    // Fire an input event so upstream React state persists the new HTML.
    span.closest('[contenteditable="true"]')?.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const COLOR_PRESETS = [
    '#FFFFFF', '#E4E4E7', '#A1A1AA', '#71717A',
    '#000000', '#3F3F46', '#8B5CF6', '#6D28D9',
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  ];

  const CARD = 'rounded-xl border border-white/[0.10] shadow-[0_16px_48px_-8px_rgba(0,0,0,0.7)] backdrop-blur-[18px] bg-[rgba(20,16,25,0.94)]';

  const iconBtn = (extra = '') =>
    `w-8 h-8 grid place-items-center rounded-md text-white/75 hover:text-white hover:bg-white/[0.06] transition-colors ${extra}`;

  return createPortal(
    <div
      className={`fixed z-[400] -translate-x-1/2 ${CARD} flex items-center gap-0.5 px-1.5 py-1`}
      style={{ top: state.top, left: state.left }}
      data-testid="inline-format-toolbar"
      data-wb-overlay="1"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} className={iconBtn()} title="Bold" data-testid="fmt-bold">
        <Bold size={13} />
      </button>
      <button onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} className={iconBtn()} title="Italic" data-testid="fmt-italic">
        <Italic size={13} />
      </button>
      <button onMouseDown={(e) => { e.preventDefault(); exec('underline'); }} className={iconBtn()} title="Underline" data-testid="fmt-underline">
        <Underline size={13} />
      </button>
      <div className="w-px h-5 bg-white/8 mx-0.5" />

      {/* Color */}
      <div className="relative">
        <button
          onMouseDown={(e) => { e.preventDefault(); saveSelection(); setOpenPop(openPop === 'color' ? null : 'color'); }}
          className={iconBtn(openPop === 'color' ? 'bg-white/[0.08] text-white' : '')}
          title="Text color"
          data-testid="fmt-color"
        >
          <Palette size={13} />
        </button>
        {openPop === 'color' && (
          <div className={`absolute top-full mt-2 left-0 ${CARD} p-2 z-10`} data-testid="fmt-color-menu">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/40 mb-1.5">Color</p>
            <div className="grid grid-cols-6 gap-1 mb-2">
              {COLOR_PRESETS.map(c => (
                <button key={c} type="button" onMouseDown={(e) => { e.preventDefault(); exec('foreColor', c); setOpenPop(null); }}
                  data-testid={`fmt-color-${c}`}
                  className="w-6 h-6 rounded-md ring-1 ring-white/15 hover:ring-white/40 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }} />
              ))}
            </div>
            <input
              type="color"
              onChange={(e) => { exec('foreColor', e.target.value); }}
              className="w-full h-7 rounded-md border border-white/10 bg-transparent cursor-pointer"
              data-testid="fmt-color-custom"
            />
          </div>
        )}
      </div>

      {/* Size */}
      <div className="relative">
        <button
          onMouseDown={(e) => { e.preventDefault(); saveSelection(); setOpenPop(openPop === 'size' ? null : 'size'); }}
          className={iconBtn(openPop === 'size' ? 'bg-white/[0.08] text-white' : '')}
          title="Font size"
          data-testid="fmt-size"
        >
          <TypeIcon size={13} />
        </button>
        {openPop === 'size' && (
          <div className={`absolute top-full mt-2 left-0 ${CARD} p-2 z-10 min-w-[180px]`} data-testid="fmt-size-menu">
            <p className="text-[9px] uppercase tracking-[0.14em] font-bold text-white/40 mb-1.5">Size (px)</p>
            <div className="grid grid-cols-4 gap-1">
              {[12, 14, 16, 18, 20, 24, 32, 48].map(sz => (
                <button
                  key={sz}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); applyInlineStyle({ fontSize: `${sz}px` }); setOpenPop(null); }}
                  data-testid={`fmt-size-${sz}`}
                  className="px-2 py-1.5 rounded-md text-[11px] font-semibold text-white/75 hover:bg-white/[0.08] hover:text-white"
                >
                  {sz}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="w-px h-5 bg-white/8 mx-0.5" />
      <button
        onMouseDown={async (e) => {
          e.preventDefault();
          saveSelection();
          const url = await themedPrompt({ message: 'Link URL:', initialValue: '' });
          if (url) exec('createLink', url);
        }}
        className={iconBtn()}
        title="Link"
        data-testid="fmt-link"
      >
        <Link2 size={13} />
      </button>
      <button
        onMouseDown={(e) => {
          e.preventDefault();
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;
          const selectedText = sel.toString().trim();
          let node = sel.getRangeAt(0).commonAncestorContainer;
          if (node.nodeType !== 1) node = node.parentNode;
          let blockEl = node;
          while (blockEl && blockEl !== document.body) {
            if (blockEl.nodeType === 1 && blockEl.getAttribute && blockEl.getAttribute('data-block-id')) break;
            blockEl = blockEl.parentNode;
          }
          if (!blockEl || blockEl === document.body) return;
          const blockId = blockEl.getAttribute('data-block-id');
          if (!blockId) return;
          let text = selectedText;
          if (!text) {
            let ce = node;
            while (ce && ce !== document.body) {
              if (ce.nodeType === 1 && ce.getAttribute && ce.getAttribute('contenteditable') === 'true') break;
              ce = ce.parentNode;
            }
            text = ce && ce.textContent ? ce.textContent.trim() : '';
          }
          window.dispatchEvent(new CustomEvent('wb:duplicate-text', {
            detail: { blockId, text: text || 'New text' }
          }));
        }}
        className={iconBtn()}
        title="Duplicate this text inside the section"
        data-testid="fmt-duplicate"
      >
        <Copy size={13} />
      </button>
    </div>,
    document.body
  );
}
